import React, { useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { FiClock, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { MdPushPin, MdOutlinePushPin } from 'react-icons/md';
import styles from '../AnnouncementsV2.module.css';

// Helper to format date
const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

// Check if announcement is "new" (within last 7 days)
const isNewAnnouncement = (publishDate) => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return new Date(publishDate) > sevenDaysAgo;
};

// Get initials from name
const getInitials = (firstName, lastName) => {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '??';
};

// Avatar component
const AnnouncementAvatar = ({ author }) => {
  if (!author) {
    return (
      <div className={`${styles.avatar} ${styles.avatarDefault}`}>
        ??
      </div>
    );
  }

  const initials = getInitials(author.first_name, author.last_name);

  return (
    <div className={`${styles.avatar} ${styles.avatarInstructor}`}>
      {initials}
    </div>
  );
};

// Author badge component
const AuthorBadge = ({ isInstructor }) => {
  return (
    <span className={`${styles.authorBadge} ${styles.badgeInstructor}`}>
      {isInstructor ? 'Instructor' : 'Staff'}
    </span>
  );
};

const AnnouncementCard = ({ announcement, onEdit, onDelete, onPin }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const author = announcement.created_by;
  const authorName = author
    ? `${author.first_name || ''} ${author.last_name || ''}`.trim() || 'Unknown'
    : 'Unknown';
  const isNew = isNewAnnouncement(announcement.publish_date);
  const displayTitle = announcement.title || 'Untitled Announcement';

  // Check if content is long enough to need truncation
  const contentLength = announcement.description?.length || 0;
  const needsTruncation = contentLength > 300;

  return (
    <div
      className={`${styles.announcementCard} ${announcement.pinned ? styles.announcementCardPinned : ''}`}
    >
      <div className={styles.announcementCardHeader}>
        <AnnouncementAvatar author={author} />

        <div className={styles.announcementCardContent}>
          <div className={styles.announcementCardTop}>
            <div className={styles.authorInfo}>
              <span className={styles.authorName}>{authorName}</span>
              <AuthorBadge isInstructor={true} />
              {isNew && <span className={styles.newBadge}>New</span>}
            </div>
            <div className={styles.announcementActions}>
              <button
                className={`${styles.actionButton} ${styles.pinButton} ${announcement.pinned ? styles.pinButtonActive : ''}`}
                onClick={onPin}
                title={announcement.pinned ? 'Unpin' : 'Pin'}
              >
                {announcement.pinned ? <MdPushPin size={18} /> : <MdOutlinePushPin size={18} />}
              </button>
              <button
                className={`${styles.actionButton} ${styles.editButton}`}
                onClick={onEdit}
                title="Edit"
              >
                <FiEdit2 size={18} />
              </button>
              <button
                className={`${styles.actionButton} ${styles.deleteButton}`}
                onClick={onDelete}
                title="Delete"
              >
                <FiTrash2 size={18} />
              </button>
            </div>
          </div>

          <div className={styles.timestamp}>
            <FiClock size={14} className={styles.timestampIcon} />
            {formatDateTime(announcement.publish_date)}
          </div>

          <h3 className={styles.announcementTitle}>{displayTitle}</h3>

          <div
            className={`${styles.announcementContent} ${!isExpanded && needsTruncation ? styles.announcementContentCollapsed : ''}`}
          >
            {ReactHtmlParser(announcement.description)}
          </div>

          {needsTruncation && (
            <button
              className={styles.readMoreLink}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementCard;
